// pms-core / domain — what makes a room fit a stay.
//
// One set of rules for the three things that ask the question: v1's proposals
// (`proposeRoomAssignment`), v2's reshuffle (`planRoomReshuffle`) and the
// day-before review (`proposeUpgrades`). Three screens that disagreed about
// whether a Twin fits room 210 would be a bug nobody notices until the desk
// trusts the wrong one. docs/PMS-TETRIS-BRIEF.md §5 and §6.3.
//
// ── The category order: standing × size (brief §6.3, operator Q9) ──────────
//
// A category has a STANDING (superior or not — `pms_room.is_superior`) and a
// SIZE (its party size, `pms_room_type.max_occupancy`). A room may host a stay
// sold as C when its category is at least C on BOTH. Measured 2026-10-01: of
// 1 678 single-room channel bookings since August 2025, 1 672 (99.6 %) slept in
// a room that dominates what they bought. So a DSTD couple may go anywhere; a
// DSUP couple only into DSUP, TSUP or FAMS; a FAM family only into FAM or FAMS.
// « Never below » is the same order read the other way.
//
// ── The bed rule (brief §5, operator Q6/Q14) ───────────────────────────────
//
// A request for d doubles and s singles is met by a layout of D doubles and S
// singles when D ≥ d and D + S ≥ d + s — every double asked for is a double,
// and there are at least as many separate beds. The operator's example, to the
// letter: a Twin fits a TSUP made up as Triple (a double and a single: two
// beds), and not a DSUP whose only layout is a King double. The first version of
// v1 compared layout CODES, which is stricter than the desk: it never offered a
// Triple-made TSUP for a Twin.

import type { BedConfig, Room, RoomType, RoomTypeId } from './types.js';

export interface CategoryProfile {
  superior: boolean;
  /** The category's party size: 2, 3 or 4 at the hotel. */
  size: number;
}

/** A layout as beds — `pms_bed_config` doubles and singles. */
export interface BedShape {
  doubles: number;
  singles: number;
}

/** The two lookups every fit question needs, built once per screen. */
export interface RoomFit {
  categories: ReadonlyMap<RoomTypeId, CategoryProfile>;
  /** null for a code the property does not define — compared by name then. */
  layoutOf: (code: string) => BedShape | null;
}

export function buildRoomFit(
  roomTypes: readonly Pick<RoomType, 'id' | 'maxOccupancy'>[],
  rooms: readonly Pick<Room, 'roomTypeId' | 'isSuperior'>[],
  bedConfigs: readonly Pick<BedConfig, 'code' | 'layout'>[],
): RoomFit {
  const categories = new Map<RoomTypeId, CategoryProfile>();
  for (const t of roomTypes) {
    const of = rooms.filter((r) => r.roomTypeId === t.id);
    // Superior only when EVERY room of it is: a category the data is unsure
    // about must not be treated as the better one.
    categories.set(t.id, { superior: of.length > 0 && of.every((r) => r.isSuperior), size: t.maxOccupancy });
  }
  const layouts = new Map(bedConfigs.map((b) => [b.code, { doubles: b.layout.doubles, singles: b.layout.singles }]));
  return { categories, layoutOf: (code) => layouts.get(code) ?? null };
}

/**
 * The category a stay was SOLD as — what « never below », « upgrade » and « back to its
 * category » are measured against. For a stay the Vega seed brought in it is the plan
 * product's category (`soldRoomTypeId`), because the seed sets `roomTypeId` to the type of the
 * room the stay is IN: read that instead and an upgrade the desk gave in Vega would pass for
 * what the guest bought, and could never be proposed back down (operator 2026-10-22).
 * Where the sold category is not known the stay's own category is the best there is.
 */
export const soldCategory = (s: { roomTypeId: RoomTypeId; soldRoomTypeId?: RoomTypeId | null }): RoomTypeId =>
  s.soldRoomTypeId ?? s.roomTypeId;

/** Whether a room of category `room` may host a stay sold as `sold`. The same
 *  category always may. A category the fit does not know never dominates:
 *  « unknown » is not a licence to upgrade or to downgrade. */
export function categoryDominates(fit: RoomFit, room: RoomTypeId | null, sold: RoomTypeId): boolean {
  if (room === sold) return true;
  if (room === null) return false;
  const r = fit.categories.get(room);
  const s = fit.categories.get(sold);
  if (r === undefined || s === undefined) return false;
  return (r.superior || !s.superior) && r.size >= s.size;
}

/** The bed rule: every double asked for is a double, and as many separate beds. */
export function layoutMeets(want: BedShape, offer: BedShape): boolean {
  return offer.doubles >= want.doubles && offer.doubles + offer.singles >= want.doubles + want.singles;
}

const meets = (fit: RoomFit, wanted: string, offer: string): boolean => {
  const w = fit.layoutOf(wanted);
  const o = fit.layoutOf(offer);
  return w === null || o === null ? wanted === offer : layoutMeets(w, o);
};

/** Whether the room CAN be made up to meet the request. No request always fits;
 *  so does a multi-space unit, whose beds are described per space and which can
 *  neither pass nor fail a configuration check. */
export function roomMeetsBed(
  fit: RoomFit,
  room: Pick<Room, 'configOptions'>,
  wanted: string | null | undefined,
): boolean {
  if (!wanted || room.configOptions.length === 0) return true;
  return room.configOptions.some((o) => meets(fit, wanted, o));
}

/** Whether the room STANDS made up to meet the request right now: true, false
 *  (the étage must change it), or null (no request, or nobody has said how the
 *  room stands). A room with a single possible layout stands in it. */
export function bedStandsFor(
  fit: RoomFit,
  room: Pick<Room, 'configOptions' | 'currentBedConfig'>,
  wanted: string | null | undefined,
): boolean | null {
  if (!wanted) return null;
  const standing = room.currentBedConfig ?? (room.configOptions.length === 1 ? room.configOptions[0]! : null);
  return standing === null ? null : meets(fit, wanted, standing);
}
